<template>
  <div class="page-container">
    <el-card>
      <!-- 搜索区 -->
      <div class="search-bar">
        <el-input v-model="query.keyword" placeholder="搜索账号/昵称" clearable style="width: 200px" @keyup.enter.native="handleSearch" />
        <el-select v-model="query.status" placeholder="管理员状态" clearable style="width: 120px">
          <el-option label="启用" :value="1" />
          <el-option label="禁用" :value="0" />
        </el-select>
        <el-button type="primary" size="mini" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button size="mini" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 操作按钮 -->
      <div class="action-bar">
        <el-button v-permission="['system:user:create']" type="success" size="mini" icon="el-icon-plus" @click="handleCreate">新建</el-button>
      </div>

      <!-- 表格 -->
      <el-table ref="table" :data="tableData" v-loading="loading" border stripe style="width: 100%">
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column label="头像" width="70" align="center">
          <template slot-scope="{ row }">
            <el-avatar :size="36" :src="row.avatar || defaultAvatar" />
          </template>
        </el-table-column>
        <el-table-column prop="username" label="账号" width="130" />
        <el-table-column prop="nickname" label="昵称" width="130" />
        <el-table-column prop="email" label="邮箱" min-width="160" />
        <el-table-column label="角色" min-width="160">
          <template slot-scope="{ row }">
            <el-tag v-for="role in row.roles" :key="role.id" size="small" style="margin-right: 4px">{{ role.name }}</el-tag>
            <span v-if="!row.roles || !row.roles.length" class="text-muted">暂无角色</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" min-width="160" />
        <el-table-column label="操作" width="220" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button v-permission="['system:user:update']" type="primary" size="mini" icon="el-icon-edit" @click="handleEdit(row)">编辑</el-button>
            <el-tooltip :disabled="!isSuperAdmin(row)" content="超级管理员角色不允许修改" placement="top">
              <span>
                <el-button v-permission="['system:user:assign']" :disabled="isSuperAdmin(row)" type="warning" size="mini" icon="el-icon-s-custom" @click="handleAssignRole(row)">角色</el-button>
              </span>
            </el-tooltip>
            <el-button v-permission="['system:user:delete']" type="danger" size="mini" icon="el-icon-delete" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        :current-page="query.page"
        :page-sizes="[10, 15, 20, 50]"
        :page-size="query.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top: 16px; text-align: right"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialog.title" :visible.sync="dialog.visible" width="520px" @close="resetForm">
      <el-form ref="userForm" :model="form" :rules="rules" label-width="80px">
        <el-form-item label="账号" prop="username">
          <el-input v-model="form.username" :disabled="!!form.id" placeholder="请输入账号（6-20位字母/数字）" />
        </el-form-item>
        <el-form-item :label="form.id ? '新密码' : '密码'" :prop="form.id ? '' : 'password'">
          <el-input v-model="form.password" type="password" :placeholder="form.id ? '不填则不修改密码' : '请输入密码（至少6位）'" show-password />
        </el-form-item>
        <el-form-item label="昵称" prop="nickname">
          <el-input v-model="form.nickname" placeholder="请输入昵称" />
        </el-form-item>
        <el-form-item label="邮箱">
          <el-input v-model="form.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="mini" @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="dialog.loading" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>

    <!-- 分配角色弹窗 -->
    <el-dialog title="分配角色" :visible.sync="roleDialog.visible" width="480px">
      <div class="role-assign">
        <p class="role-user-info">管理员：<strong>{{ roleDialog.user.username }}</strong>（{{ roleDialog.user.nickname }}）</p>
        <el-checkbox-group v-model="roleDialog.selectedRoles">
          <el-checkbox
            v-for="role in allRoles"
            :key="role.id"
            :label="role.id"
            class="role-checkbox"
          >{{ role.name }}（{{ role.code }}）</el-checkbox>
        </el-checkbox-group>
      </div>
      <div slot="footer">
        <el-button size="mini" @click="roleDialog.visible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="roleDialog.loading" @click="handleRoleSubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getUserList, createUser, updateUser, deleteUser, assignUserRole, getUserDetail } from '@/api/user'
import { getAllRoles } from '@/api/role'

export default {
  name: 'UserManage',
  data() {
    return {
      defaultAvatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
      loading: false,
      tableData: [],
      total: 0,
      query: { page: 1, pageSize: 15, keyword: '', status: '' },
      dialog: { visible: false, title: '', loading: false },
      form: { id: null, username: '', password: '', nickname: '', email: '', status: 1 },
      rules: {
        username: [{ required: true, message: '账号不能为空', trigger: 'blur' }],
        password: [{ required: true, message: '密码不能为空', trigger: 'blur' }, { min: 6, message: '密码至少6位', trigger: 'blur' }],
        nickname: [{ required: true, message: '昵称不能为空', trigger: 'blur' }]
      },
      allRoles: [],
      roleDialog: { visible: false, loading: false, user: {}, selectedRoles: [] }
    }
  },
  created() {
    this.fetchData()
    this.fetchAllRoles()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const res = await getUserList(this.query)
        this.tableData = res.data.list
        this.total = res.data.total
        this.$nextTick(() => this.$refs.table?.doLayout?.())
      } finally {
        this.loading = false
      }
    },
    async fetchAllRoles() {
      const res = await getAllRoles()
      this.allRoles = res.data || []
    },
    isSuperAdmin(row) {
      return !!(row.roles && row.roles.some(r => r.code === 'super_admin'))
    },
    handleSearch() { this.query.page = 1; this.fetchData() },
    handleReset() { this.query = { page: 1, pageSize: 15, keyword: '', status: '' }; this.fetchData() },
    handleSizeChange(val) { this.query.pageSize = val; this.fetchData() },
    handleCurrentChange(val) { this.query.page = val; this.fetchData() },
    handleCreate() {
      this.form = { id: null, username: '', password: '', nickname: '', email: '', status: 1 }
      this.dialog = { visible: true, title: '新增管理员', loading: false }
    },
    async handleEdit(row) {
      const res = await getUserDetail({ id: row.id })
      const data = res.data
      this.form = { id: data.id, username: data.username, password: '', nickname: data.nickname, email: data.email, status: data.status }
      this.dialog = { visible: true, title: '编辑管理员', loading: false }
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`确认删除管理员「${row.username}」？`, '提示', { type: 'warning' })
        await deleteUser({ id: row.id })
        this.$message.success('删除成功')
        this.fetchData()
      } catch {}
    },
    handleAssignRole(row) {
      this.roleDialog.user = row
      this.roleDialog.selectedRoles = (row.roles || []).map(r => r.id)
      this.roleDialog.visible = true
      this.roleDialog.loading = false
    },
    async handleRoleSubmit() {
      this.roleDialog.loading = true
      try {
        await assignUserRole({ id: this.roleDialog.user.id, roleIds: this.roleDialog.selectedRoles })
        this.$message.success('角色分配成功')
        this.roleDialog.visible = false
        this.fetchData()
      } finally {
        this.roleDialog.loading = false
      }
    },
    handleSubmit() {
      this.$refs.userForm.validate(async valid => {
        if (!valid) return
        this.dialog.loading = true
        try {
          if (this.form.id) {
            await updateUser(this.form)
            this.$message.success('更新成功')
          } else {
            await createUser(this.form)
            this.$message.success('创建成功')
          }
          this.dialog.visible = false
          this.fetchData()
        } finally {
          this.dialog.loading = false
        }
      })
    },
    resetForm() {
      this.$refs.userForm && this.$refs.userForm.resetFields()
    }
  }
}
</script>

<style lang="scss" scoped>
.text-muted { color: #c0c4cc; font-size: 12px; }
.role-assign {
  .role-user-info { margin: 0 0 16px; color: #606266; }
  .role-checkbox { display: block; margin-bottom: 12px; }
}
</style>
